import { AppData } from "../../AppDataProvider/types";
import { ServicePart } from "../../GeneratedGraphQL";
import { emitEvent, StepEventNames } from "./emitEvent";

/**
 * Emit an event for a change in step in the booking flow. All of the data
 * emitted except for `iFrameId` is optional as we do not know what position
 * the step might be in the booking flow.
 *
 * @internal
 */
export function emitStepEvent(appData: AppData, name: StepEventNames): void {
  if (!appData.iFrameId) {
    return;
  }

  emitEvent({
    data: {
      attendees: appData.bookingData.attendees,
      bookingNotes: appData.bookingData.bookingNotes,
      bookingQuestionData: appData.bookingData.bookingQuestionData,
      bookingQuestionFileNameData:
        appData.bookingData.bookingQuestionFileNameData,
      confirmedBookingIds: appData.bookingData.confirmedBookingIds,
      createdSource: appData.bookingData.createdSource,
      discountCode: appData.bookingData.discountCode,
      iFrameId: appData.iFrameId,
      payNow: appData.bookingData.payNow,
      reservedSpaceId: appData.bookingData.reservedSpaceId,
      resource: appData.bookingData.resource,
      resourceGroupId: appData.bookingData.resourceGroupId,
      resourceIds: appData.bookingData.resourceIds,
      selectedBookings: appData.bookingData.selectedBookings,
      selectedTimeSlot: appData.bookingData.selectedTimeSlot,
      sessionId: appData.bookingData.sessionId,
      startDate: appData.bookingData.startDate,
      tier: appData.bookingData.tier,
      timezone: appData.bookingData.timezone,
      totalPaid: appData.bookingData.totalPaid,

      ...(appData.bookingData.bookings && {
        bookings: appData.bookingData.bookings.map((booking) => ({
          _id: booking._id,
          end: booking.end,
          price: booking.price,
          recurring: booking.recurring,
          remainingSpaces: booking.remainingSpaces,
          start: booking.start,
        })),
      }),

      ...(appData.bookingData.duration && {
        duration: {
          default: appData.bookingData.duration.default,
          minutes: appData.bookingData.duration.minutes,
          oldPrice: appData.bookingData.duration.oldPrice,
          price: appData.bookingData.duration.price,
        },
      }),

      ...(appData.bookingData.organisation && {
        organisation: {
          _id: appData.bookingData.organisation._id,
          name: appData.bookingData.organisation.name,
          slug: appData.bookingData.organisation.slug,
        },
      }),

      ...(appData.bookingData.pricePoints && {
        pricePoints: appData.bookingData.pricePoints.map((pricePoint) => ({
          pricePoint: {
            _id: pricePoint.pricePoint._id,
            name: pricePoint.pricePoint.name,
            pricePerQuantity: pricePoint.pricePoint.pricePerQuantity,
            spacesPerQuantity: pricePoint.pricePoint.spacesPerQuantity,
          },
          quantity: pricePoint.quantity,
        })),
      }),

      ...(appData.bookingData.service && {
        service: {
          _id: appData.bookingData.service._id,
          availabilityDisplayMode:
            appData.bookingData.service.availabilityDisplayMode,
          bookingDepositAmount:
            appData.bookingData.service.bookingDepositAmount,
          bookingPaymentOption:
            appData.bookingData.service.bookingPaymentOption,
          bookingPaymentOptional:
            appData.bookingData.service.bookingPaymentOptional,
          bookingVisibilityType:
            appData.bookingData.service.bookingVisibilityType,
          category: {
            _id: appData.bookingData.service.category._id,
            name: appData.bookingData.service.category.name,
            order: appData.bookingData.service.category.order,
          },
          description: appData.bookingData.service.description,
          image: appData.bookingData.service.image,
          isDiscountCodeAvailable:
            appData.bookingData.service.isDiscountCodeAvailable,
          name: appData.bookingData.service.name,
          numberOfResources: appData.bookingData.service.numberOfResources,
          occupancy: appData.bookingData.service.occupancy,
          resourceAllocationType:
            appData.bookingData.service.resourceAllocationType,
          tiers: appData.bookingData.service.tiers,

          ...(appData.bookingData.service.durations && {
            durations: appData.bookingData.service.durations.map(
              (duration) => ({
                default: duration.default,
                minutes: duration.minutes,
                oldPrice: duration.oldPrice,
                POA: duration.POA,
                price: duration.price,
              }),
            ),
          }),

          ...(appData.bookingData.service.parts && {
            parts: appData.bookingData.service.parts
              .filter((part): part is ServicePart => !!part)
              .map((part) => ({
                duration: part.duration,
                durationType: part.durationType,
                label: part.label,
                type: part.type,
              })),
          }),
        },
      }),

      ...(appData.bookingData.userDetails && {
        userDetails: {
          crmFieldData: appData.bookingData.userDetails.crmFieldData,
          email: appData.bookingData.userDetails.email,
          firstName: appData.bookingData.userDetails.firstName,
          lastName: appData.bookingData.userDetails.lastName,
          marketingOptIn: appData.bookingData.userDetails.marketingOptIn,
          phone: appData.bookingData.userDetails.phone,
        },
      }),
    },
    name,
  });
}
